import { useAppDispatch } from 'legacy/lut/hooks/useAppDispatch'
import { useCallback, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { useSafetyTranslation } from 'cng-common/features/translations'
import { type IModalContent, ModalDialog } from 'core-components/library/modalDialog'
import { Card } from 'partner360/components/card'
import { registerPlanSectionDeckSlot } from 'partner360/lib/planSectionDialog'

import { FINANCIAL_COCKPIT_TRANSLATION_KEYS } from '../../../../configs/translations'
import { findAttentionNavigatorPath, type TAttentionNavigatorMode } from '../../../../lib'
import { setCockpitAttentionFullscreen } from '../../../../model/financialCockpitSlice'
import {
  selectCockpitAttentionDetailPartner,
  selectCockpitAttentionSelectedStatus,
  selectCockpitIsAttentionFullscreen,
  selectCockpitOffering,
} from '../../../../model/selectors'
import { CockpitCardHeader } from '../../../components/cockpitCardHeader'
import { CockpitFilterSummary } from '../../../components/cockpitFilterSummary'
import { CockpitIconButton } from '../../../components/cockpitIconButton'
import { useAttentionScopePbmCount } from '../../hooks/useAttentionScopePbmCount'
import { useAttentionScopeSize } from '../../hooks/useAttentionScopeSize'
import { PARTNER_UNIT, PBM_UNIT } from '../../hooks/useAttentionStatusesText'
import { AttentionCount } from '../attentionCount'

import { AttentionFullscreenNavigator } from './AttentionFullscreenNavigator'
import { AttentionFullscreenOverview } from './AttentionFullscreenOverview'
import { AttentionFullscreenPartnerCard } from './AttentionFullscreenPartnerCard'
import { useAttentionFullscreenData } from './useAttentionFullscreenData'

import styles from './AttentionStatusesFullscreen.module.scss'

const KEYS = FINANCIAL_COCKPIT_TRANSLATION_KEYS.attentionStatuses

interface IFullscreenHeaderProps {
  title: string
  onClose: () => void
}

/** The tile's own header, by the same rules: name and scope on the left, acts on the right. */
const FullscreenHeader = ({ title, onClose }: IFullscreenHeaderProps) => {
  const t = useSafetyTranslation()
  const scopeSize = useAttentionScopeSize(true)
  const scopePbmCount = useAttentionScopePbmCount(true)

  return (
    <CockpitCardHeader
      title={title}
      description={
        <>
          <CockpitFilterSummary expanded bordered={false} offering category />
          {scopeSize > 0 && (
            <span className={styles.scope}>
              <span aria-hidden>&middot;</span>
              <AttentionCount count={scopeSize} unit={PARTNER_UNIT} />
              <span aria-hidden>&middot;</span>
              <AttentionCount count={scopePbmCount} unit={PBM_UNIT} />
            </span>
          )}
        </>
      }
      actions={
        <CockpitIconButton
          icon="kbase kbase-close"
          label={t(KEYS.fullscreen.close)}
          onClick={onClose}
        />
      }
    />
  )
}

/**
 * What the full-screen view is made of: a navigator of people or places on the left, and in the
 * middle either the picked level with its partners or one partner's card. The offering is the
 * cockpit's, switched from the plan deck over the dialog; the drill-down is the dialog's own, so
 * the tile below keeps its own.
 */
const FullscreenBody = () => {
  const { statuses, partners, overviews, ownersByPartner, modes, trees, isPbm } =
    useAttentionFullscreenData()
  const tileStatus = useSelector(selectCockpitAttentionSelectedStatus)
  const tileDetailPartner = useSelector(selectCockpitAttentionDetailPartner)
  /* A PBM who opens the view from a partner's status in the tile lands on that partner and status. */
  const [initialDetail] = useState(() =>
    isPbm && tileStatus && tileDetailPartner ? tileDetailPartner : null
  )
  const [mode, setMode] = useState<TAttentionNavigatorMode>(modes[0])
  const [nodeKey, setNodeKey] = useState<string | null>(null)
  const [partnerId, setPartnerId] = useState<string | null>(null)
  const [selectedBit, setSelectedBit] = useState<number | null>(() =>
    initialDetail ? (tileStatus?.bitValue ?? null) : null
  )
  /* A status belongs to the offering it was picked in: another offering drops it. */
  const selectedOffering = useSelector(selectCockpitOffering)
  const [bitOffering, setBitOffering] = useState(selectedOffering)

  if (bitOffering !== selectedOffering) {
    setBitOffering(selectedOffering)
    setSelectedBit(null)
  }

  /* The counters count the partners that hold the picked status, or anything at all. */
  const scopeIds = useMemo(() => {
    const status = statuses.find(({ bitValue }) => bitValue === selectedBit)

    if (status) {
      return new Set(status.partnerIds)
    }

    return new Set(
      [...overviews.values()]
        .filter(({ open }) => open.length > 0)
        .map(({ partner }) => partner.partnerId)
    )
  }, [overviews, selectedBit, statuses])

  const nodes = trees[mode]
  const path = useMemo(
    () => (nodeKey ? findAttentionNavigatorPath(nodes, nodeKey) : []),
    [nodeKey, nodes]
  )
  const node = path[path.length - 1]
  const allPartnerIds = useMemo(() => partners.map(({ partnerId: id }) => id), [partners])

  const handleModeChange = useCallback((next: TAttentionNavigatorMode) => {
    setMode(next)
    setNodeKey(null)
    setPartnerId(null)
  }, [])

  const partnerOverview = mode === 'partners' && partnerId ? overviews.get(partnerId) : undefined

  return (
    <>
      <div className={styles.body}>
        {/* A PBM's partners are one list of their own, so there is nothing to navigate: the list
            takes the width and a picked partner opens to be worked on. */}
        {!isPbm && (
          <AttentionFullscreenNavigator
            modes={modes}
            mode={mode}
            onModeChange={handleModeChange}
            nodes={nodes}
            scopeIds={scopeIds}
            selectedKey={nodeKey}
            onSelect={setNodeKey}
            overviews={overviews}
            selectedPartnerId={partnerId}
            onSelectPartner={setPartnerId}
          />
        )}
        <div className={styles.main}>
          {partnerOverview ? (
            <AttentionFullscreenPartnerCard
              overview={partnerOverview}
              owners={ownersByPartner.get(partnerOverview.partner.partnerId)}
            />
          ) : (
            <AttentionFullscreenOverview
              owner={node?.owner}
              scope={
                node?.label
                  ? { label: node.label }
                  : { labelKey: node?.labelKey ?? KEYS.fullscreen.root[mode] }
              }
              crumbs={[
                { key: null, labelKey: KEYS.fullscreen.root[mode] },
                ...path
                  .slice(0, -1)
                  .filter(({ isHeading }) => !isHeading)
                  .map(({ key, label }) => ({ key, label })),
              ]}
              onNavigate={setNodeKey}
              partnerIds={node?.partnerIds ?? allPartnerIds}
              statuses={statuses}
              overviews={overviews}
              ownersByPartner={ownersByPartner}
              selectedBit={selectedBit}
              onSelectStatus={setSelectedBit}
              showOwners={modes.length > 1}
              opensPartnerWorkspace={isPbm}
              initialDetail={initialDetail}
            />
          )}
        </div>
      </div>
    </>
  )
}

interface IFullscreenCardProps extends IModalContent {
  title: string
}

const FullscreenCard = ({ title, closeModal }: IFullscreenCardProps) => (
  <>
    {/* The cockpit draws its plan deck here, over the scrim at the top of the screen. */}
    <div ref={registerPlanSectionDeckSlot} className={styles.deckSlot} />
    <Card
      as="div"
      expanded
      hoverable={false}
      className={styles.card}
      bodyClassName={styles.cardBody}
      header={
        /* The same wrapper a board tile gives its header, so the controls stand on the right edge. */
        <div className={styles.heading}>
          <FullscreenHeader title={title} onClose={closeModal} />
        </div>
      }
    >
      <FullscreenBody />
    </Card>
  </>
)

interface IAttentionStatusesFullscreenProps {
  title: string
}

/**
 * The manager cockpit's attention tile over the whole screen, drawn as the tile's own card, for
 * finding the people, places and partners with the most left open.
 */
export const AttentionStatusesFullscreen = ({ title }: IAttentionStatusesFullscreenProps) => {
  const dispatch = useAppDispatch()
  const isOpen = useSelector(selectCockpitIsAttentionFullscreen)
  const handleClose = useCallback(() => dispatch(setCockpitAttentionFullscreen(false)), [dispatch])

  return (
    <ModalDialog
      show={isOpen}
      size="fullscreen"
      isContainerRestricted={false}
      closeOnOutsideClick={false}
      /* The card carries its own header and its own way out, so the dialog's are dropped. */
      classes={{
        overlay: styles.overlay,
        window: styles.window,
        header: styles.modalHeader,
        body: styles.modalBody,
      }}
      content={FullscreenCard}
      contentProps={{ title }}
      onClose={handleClose}
    />
  )
}

AttentionStatusesFullscreen.displayName = 'AttentionStatusesFullscreen'
